import { useState, useEffect } from 'react';
import { storyGeneratorAPI } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import { Wand2, Loader2, Download, Trash2, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const StoryGenerator = () => {
  const [brief, setBrief] = useState('');
  const [generating, setGenerating] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [expandedPrompt, setExpandedPrompt] = useState(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const res = await storyGeneratorAPI.getHistory();
      setHistory(res.data || []);
    } catch {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    if (!brief.trim()) {
      toast.error('Please enter your story idea');
      return;
    }
    try {
      setGenerating(true);
      setCurrentResult(null);
      const res = await storyGeneratorAPI.generate(brief.trim());
      setCurrentResult(res.data);
      setBrief('');
      toast.success('Story generated!');
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await storyGeneratorAPI.delete(id);
      setHistory(prev => prev.filter(s => s.id !== id));
      if (currentResult?.id === id) setCurrentResult(null);
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleDownload = async (storyId) => {
    try {
      const response = await api.get(`/story-generator/${storyId}/download`, {
        responseType: 'blob',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.download = `story-${storyId}.png`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      toast.error('Download failed');
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-10" data-testid="story-generator-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
          Story Generator
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Turn a brief idea into a premium Instagram Story creative for TheLocalJewel
        </p>
      </div>

      {/* Input Section */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4" data-testid="story-input-section">
        <label className="block text-sm font-medium text-zinc-700" htmlFor="story-brief">
          What's your story idea?
        </label>
        <textarea
          id="story-brief"
          data-testid="story-brief-input"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder="e.g. Solitaire ring comparison poll — oval vs round, which one wins?"
          rows={4}
          disabled={generating}
          className="w-full px-4 py-3 border border-zinc-200 rounded-md text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-none transition-all disabled:opacity-50"
        />
        <Button
          onClick={handleGenerate}
          disabled={generating || !brief.trim()}
          data-testid="generate-story-btn"
          className="bg-zinc-900 text-white hover:bg-zinc-800 px-6 py-2.5 text-sm font-medium rounded-md transition-all disabled:opacity-40"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Wand2 className="w-4 h-4 mr-2" />
              Generate Now
            </>
          )}
        </Button>
      </div>

      {/* Loading state */}
      {generating && (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 flex flex-col items-center justify-center space-y-4" data-testid="generating-state">
          <div className="relative">
            <div className="w-16 h-16 border-2 border-zinc-200 rounded-full" />
            <div className="absolute inset-0 w-16 h-16 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-zinc-900">Crafting your story</p>
            <p className="text-xs text-zinc-500 mt-1">Claude is writing the creative brief, then Gemini will render it...</p>
          </div>
        </div>
      )}

      {/* Current Result */}
      {currentResult && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="current-result">
          <div className="p-5 border-b border-zinc-100">
            <p className="text-xs uppercase tracking-widest text-zinc-400">Latest Generation</p>
            <p className="text-sm text-zinc-700 mt-1">{currentResult.brief}</p>
          </div>
          <div className="flex flex-col md:flex-row">
            {/* Image */}
            {currentResult.image_url ? (
              <div className="md:w-1/2 p-5 flex items-center justify-center bg-zinc-50">
                <img
                  src={currentResult.image_url}
                  alt="Generated Story"
                  data-testid="generated-story-image"
                  className="max-h-[500px] rounded-md shadow-sm object-contain"
                />
              </div>
            ) : (
              <div className="md:w-1/2 p-5 flex items-center justify-center bg-zinc-50">
                <p className="text-sm text-zinc-400">Image generation failed — prompt was saved</p>
              </div>
            )}
            {/* Prompt */}
            <div className="md:w-1/2 p-5 space-y-3">
              <p className="text-xs uppercase tracking-widest text-zinc-400">Creative Prompt</p>
              <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap max-h-[400px] overflow-y-auto">
                {currentResult.polished_prompt}
              </p>
              {currentResult.image_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(currentResult.id)}
                  data-testid="download-current-btn"
                  className="text-zinc-700 border-zinc-200"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Download
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div data-testid="story-history-section">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-medium text-zinc-700">Previous Generations</h2>
        </div>

        {loadingHistory ? (
          <div className="text-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-400 mx-auto" />
          </div>
        ) : history.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center">
            <p className="text-sm text-zinc-400">No stories generated yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((story) => (
              <div
                key={story.id}
                className="bg-white border border-zinc-200 rounded-lg overflow-hidden"
                data-testid={`history-item-${story.id}`}
              >
                <div className="p-4 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-900 font-medium truncate">{story.brief}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{formatDate(story.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {story.image_url && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-400 hover:text-zinc-700"
                        onClick={() => handleDownload(story.id)}
                        data-testid={`download-${story.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-zinc-400 hover:text-red-600"
                      onClick={() => handleDelete(story.id)}
                      data-testid={`delete-${story.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn("h-8 w-8 text-zinc-400 hover:text-zinc-700")}
                      onClick={() => setExpandedPrompt(expandedPrompt === story.id ? null : story.id)}
                    >
                      {expandedPrompt === story.id ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </div>

                {expandedPrompt === story.id && (
                  <div className="border-t border-zinc-100 flex flex-col md:flex-row">
                    {story.image_url && (
                      <div className="md:w-1/3 p-4 bg-zinc-50 flex items-center justify-center">
                        <img
                          src={story.image_url}
                          alt="Story"
                          className="max-h-[300px] rounded object-contain"
                        />
                      </div>
                    )}
                    <div className={cn("p-4", story.image_url ? "md:w-2/3" : "w-full")}>
                      <p className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Creative Prompt</p>
                      <p className="text-xs text-zinc-600 leading-relaxed whitespace-pre-wrap max-h-[250px] overflow-y-auto">
                        {story.polished_prompt}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryGenerator;
